/// <amd-module name="DistributedTaskUI/ServiceEndpoints/Editor/UIContributions/AzureRMEndpoint/Components/AzureRMManualFederationEndpointEditorComponent" />
import * as React from "react";
import * as EndpointConstants from "ServiceEndpoints/Client/Constants/ServiceEndpoint";
import { ServiceEndpoint } from "ServiceEndpoints/Client/Contracts/ServiceEndpoint";
import { AzureRMKeys } from "DistributedTaskUI/ServiceEndpoints/Editor/UIContributions/AzureRMEndpoint/AzureRMEndpointConstants";
import { ActionCreatorManager } from "DistributedTask/Flux/ActionCreatorManager";
import { AzureRMActionCreator } from "DistributedTaskUI/ServiceEndpoints/Editor/UIContributions/AzureRMEndpoint/Actions/AzureRMActionCreator";
import { GenericFooterComponent } from "DistributedTaskUI/ServiceEndpoints/Editor/Components/GenericEndpointComponents/GenericFooterComponent";
import { IEndpointEditorComponentProps } from "DistributedTaskUI/ServiceEndpoints/Editor/Components/EndpointsEditorPanelInterfaces";
import { EndpointsEditorActionsCreator } from "DistributedTaskUI/ServiceEndpoints/Editor/Actions/EndpointsEditorActionsCreator";
import { EndpointsEditorStore } from "DistributedTaskUI/ServiceEndpoints/Editor/Stores/EndpointsEditorStore";
import { ActionsCreatorInstanceIds } from "DistributedTaskUI/ServiceEndpoints/Editor/Constants";
import { StoreManager } from "DistributedTask/Flux/StoreManager";
import { ObservableValue } from "VSS/Core/Observable";
import { VssComponent, IVssComponentContext } from "VSS/Platform/Layout";
import { cloneEndpoint } from "DistributedTaskUI/ServiceEndpoints/Editor/Utils/EndpointsEditorPanelUtils";
import * as Resources from "DistributedTaskUI/ServiceEndpoints/Editor/Resources";
import { GenericAuthorizationComponent } from "DistributedTaskUI/ServiceEndpoints/Editor/Components/GenericEndpointComponents/GenericAuthorizationComponent";
import { AzureRMDraftEndpointBasicsComponent } from "DistributedTaskUI/ServiceEndpoints/Editor/UIContributions/AzureRMEndpoint/Components/AzureRMDraftEndpointBasicsComponent";
import * as Utils_String from "VSS/Core/Util/String";
import * as Utils_Object from "VSS/Core/Util/Object";
import { MessageCard, MessageCardSeverity } from "VSSUI/MessageCard";
import { InputDescriptor } from "ServiceEndpoints/Client/Contracts/FormInput_ServiceEndpoint";
import { FormItem } from "VSSUI/FormItem";
import { Link } from "VSSUI/Link";
import { TextField } from "VSSUI/TextField";
import { copyToClipboard } from "VSSUI/Clipboard";
import * as Constants from "DistributedTaskUI/ServiceEndpoints/Editor/Constants";
import { IGlobalMessagesService } from "TFS/GlobalBanner/GlobalMessagesService";
import { getTFSPageData } from "Tfs/Platform/Page";
import { deepMerge } from "VSS/Platform/Util/Object";


interface IAzurRMComponentProps extends IEndpointEditorComponentProps {
    hidePanelFooter: (hideFooter: boolean) => void;
    closePanel: (serviceEndpoint: ServiceEndpoint) => void;
    onRefresh: () => void;
    onBackButtonClick: () => void;
    hideBackButton: boolean;
    updateError: (isError: boolean, errorMessage: string, errorStatus?: string) => void;
    getInvalidInputCount: () => number;
    errorMessage: string;
}

export interface IAzurRMComponentState {
    saveInProgress: boolean;
    setupInProgress: boolean;
    isError: boolean;
    errorMessage: string;
    activityId?: string;
    verifyInProgress: boolean;
    creationStep: CreationStep;
    isEndpointValid: boolean;
    isDraftValid: boolean;
    lastAction: LastAction;
}

enum LastAction {
    NONE,
    VERIFY_AND_SAVE,
    SAVE_AS_DRAFT,
    SAVE
}

enum CreationStep {
    CREATE_DRAFT,
    FINALIZE_DRAFT,
    EDIT
}

const scheme = "sc://";
const valueComponentSeparator = "/";

export class AzureRMManualFederationEndpointEditorComponent extends VssComponent<IAzurRMComponentProps, IAzurRMComponentState> {

    constructor(props: IAzurRMComponentProps, context: IVssComponentContext) {
        super(props, context);
        this._invalidInputsCount = new ObservableValue(0);
        this._malformedInputs = new ObservableValue(new Set<string>());
        this._endpointEditorActionCreator = ActionCreatorManager.GetActionCreator<EndpointsEditorActionsCreator>(EndpointsEditorActionsCreator, ActionsCreatorInstanceIds.EndpointsEditorActionsCreatorInstanceId);
        this._endpointEditorStore = StoreManager.GetStore<EndpointsEditorStore>(EndpointsEditorStore, ActionsCreatorInstanceIds.EndpointsEditorStoreInstanceId);
        this._endpointEditorStore.addChangedListener(this._onEndpointEditorStoreChange);
        this._projectId = getTFSPageData(context.pageContext).project.id;

        // this mean's we're handling everything ourselves
        this.props.hidePanelFooter(true);

        this._originalEndpoint = deepMerge({}, this.props.draftEndpoint);

        this.state = {
            saveInProgress: false,
            setupInProgress: false,
            isError: false,
            errorMessage: "",
            verifyInProgress: false,
            creationStep: this._determineStep(),
            isDraftValid: true,
            isEndpointValid: this.props.isUpdateForm,
            lastAction: LastAction.NONE
        };
    }

    private _determineStep(): CreationStep {
        const exists = !!this.props.draftEndpoint.id;
        if (exists) {
            if (this._isDraft()) {
                return CreationStep.FINALIZE_DRAFT;
            } else {
                return CreationStep.EDIT;
            }
        } else {
            return CreationStep.CREATE_DRAFT;
        }
    }

    private _isDraft = () => Utils_String.equals(this.props.draftEndpoint.data[EndpointConstants.AzureEndpointConstants.IsDraft], "True", true);

    public componentWillUnmount() {
        ActionCreatorManager.DeleteActionCreator(AzureRMActionCreator, this._actionCreatorKey);
        this._endpointEditorStore.removeChangedListener(this._onEndpointEditorStoreChange);
    }

    public render(): React.ReactNode {
        return <div className="endpoint-editor-panel-content azure-rm-workload-identity-federation-container padding-0">
        { this._renderStep() }
        </div>;
    }

    private _renderStep(): React.ReactNode {
        switch (this.state.creationStep) {
            case CreationStep.CREATE_DRAFT:
                return this._renderCreateDraft();
            case CreationStep.FINALIZE_DRAFT:
                return this._renderFinalizeDraft();
            case CreationStep.EDIT:
                return this._renderEdit();
        }
    }

    private _renderCreateDraft(): React.ReactNode {
        return <React.Fragment>
            <div role="heading" aria-level={4} className="service-endpoint-step-header">{Resources.WorkloadIdentityFederationStep1Title}</div>

            <AzureRMDraftEndpointBasicsComponent
                {...this.props}
                hideHeader={true}
                draftEndpoint={this.props.draftEndpoint}
                isUpdateForm={this.props.isUpdateForm}
                updateValidAndDirtyStateChange={this._handleValidAndDirtyStateChangeForEndpointDetails}
                instanceId={"EndpointDetailsComponent"}
                updateAllowAllPipelinesFlag={this.props.updateAllowAllPipelinesFlag}
                allowAllPipelinesFlagState={this.props.allowAllPipelinesFlagState}
            />

            {this._renderErrorWidget()}

            <GenericFooterComponent
                        isUpdateForm={this.props.isUpdateForm}
                        isVerifiable={false}
                        showNextButton={true}
                        showSaveButton={false}
                        enableSave={false}
                        enableNext={this.state.isEndpointValid}
                        saveInProgress={this.state.saveInProgress}
                        serviceEndpoint={this.props.draftEndpoint}
                        showBackButton={!this.props.hideBackButton}
                        onNextButtonClick={() => this._saveServiceEndpoint(this.props.isUpdateForm, true)}
                        onBackButtonClick={this.props.onBackButtonClick}
                    />
        </React.Fragment>;
    }

    private _renderFinalizeDraft(): React.ReactNode {
        const learnMoreHref = "https://aka.ms/azdo-rm-workload-identity/";

        return <React.Fragment>
                <MessageCard
                    className="flex-self-stretch margin-bottom-16"
                    severity={MessageCardSeverity.Info}
                   >
                    <div>
                        <span>{Resources.AzureRMOidcFederationDraftMessage}</span>&nbsp;
                        <Link target="_blank" href={learnMoreHref}>{Resources.LearnMoreText}</Link>
                    </div>
                </MessageCard>

                <div role="heading" aria-level={4} className="service-endpoint-step-header">{Resources.WorkloadIdentityFederationStep2Title}</div>

                {this._renderFederationDetails()}

                <GenericAuthorizationComponent
                    disableAllPipelinesSetting={this.props.disableAllPipelinesSetting}
                    serviceEndpointType={this.props.serviceEndpointType}
                    draftEndpoint={this.props.draftEndpoint}
                    isUpdateForm={this.props.isUpdateForm && !this._isDraft}
                    updateDraftEndpointState={this.props.updateDraftEndpointState}
                    updateInvalidInputsCount={this._updateInvalidInputsCount}
                    onValidationChange={this._onValidationChange}
                />

                {this._renderErrorWidget()}

                <GenericFooterComponent
                        isUpdateForm={this.props.isUpdateForm}
                        isVerifiable={true}
                        showSaveButton={true}
                        showSaveAsDraftButton={true}
                        showBackButton={false}
                        showNextButton={false}
                        enableSave={this.state.isEndpointValid}
                        enableSaveAsDraft={this.state.isDraftValid}
                        serviceEndpoint={this.props.draftEndpoint}
                        verifyInProgress={this.state.verifyInProgress}
                        saveInProgress={this.state.saveInProgress}
                        setupInProgress={this.state.setupInProgress}
                        onVerifyAndSaveButtonClick={() => this._saveServiceEndpoint(true, false)}
                        onSaveButtonClick={() => this._saveServiceEndpoint(true, true)}
                  />
            </React.Fragment>;
        }

    private _renderEdit(): React.ReactNode {
        return <React.Fragment>
             <div role="heading" aria-level={4} className="service-endpoint-step-header">{Resources.WorkloadIdentityFederationSectionBasics}</div>

             <AzureRMDraftEndpointBasicsComponent
                {...this.props}
                hideHeader={true}
                draftEndpoint={this.props.draftEndpoint}
                isUpdateForm={this.props.isUpdateForm}
                updateValidAndDirtyStateChange={this._handleValidAndDirtyStateChangeForEndpointDetails}
                instanceId={"EndpointDetailsComponent"}
                updateAllowAllPipelinesFlag={this.props.updateAllowAllPipelinesFlag}
                allowAllPipelinesFlagState={this.props.allowAllPipelinesFlagState}
                warnOnNameChange={true}
                hasNameChanged={this._hasFederationSubjectChanged()}
            />

            <br />

            <div role="heading" aria-level={4} className="service-endpoint-step-header">{Resources.WorkloadIdentityFederationSectionServicePrincipalDetails}</div>

            {this._renderFederationDetails()}

            <GenericAuthorizationComponent
                disableAllPipelinesSetting={this.props.disableAllPipelinesSetting}
                serviceEndpointType={this.props.serviceEndpointType}
                draftEndpoint={this.props.draftEndpoint}
                isUpdateForm={this.props.isUpdateForm && !this._isDraft}
                updateDraftEndpointState={this.props.updateDraftEndpointState}
                updateInvalidInputsCount={this._updateInvalidInputsCount}
                onValidationChange={this._onValidationChange}
            />

            {this._renderErrorWidget()}

                <GenericFooterComponent
                    isUpdateForm={this.props.isUpdateForm}
                    isVerifiable={true}
                    showSaveButton={!this._hasFederationSubjectChanged()}
                    showBackButton={true}
                    showSaveAsDraftButton={this._hasFederationSubjectChanged()}
                    enableSaveAsDraft={this.state.isEndpointValid}
                    enableSave={this.state.isEndpointValid}
                    verifyInProgress={this.state.verifyInProgress}
                    serviceEndpoint={this.props.draftEndpoint}
                    saveInProgress={this.state.saveInProgress}
                    setupInProgress={this.state.setupInProgress}
                    onSaveButtonClick={() => this._hasFederationSubjectChanged() ? this._saveServiceEndpoint(true, true, false) : this._saveServiceEndpoint(true, false, false)}
                    onVerifyAndSaveButtonClick={() => this._saveServiceEndpoint(true, false)}
                    onBackButtonClick={() => this._closePanel(this._originalEndpoint)}
                />
        </React.Fragment>;
    }

    private _renderFederationDetails = () => <React.Fragment>
        {this._renderFederationIssuer()}
        <div className="margin-vertical-8" />
        {this._renderFederationSubject()}
    </React.Fragment>

    private _renderErrorWidget = () => this.state.isError && <MessageCard
            className="flex-self-stretch margin-top-16 margin-bottom-16"
            severity={MessageCardSeverity.Error}
            >
            {this.state.errorMessage}
            <br />
            {this.state.activityId ? "ActivityId: " + this.state.activityId : null}
        </MessageCard>

    private _renderFederationSubject = () => <FormItem
        className={"endpoint-editor-panel-text-box"}
        label={Resources.WorkloadIdentityFederationSubjectLabel}
        message={Resources.WorkloadIdentityFederationSubjectDescription}
        ariaLabel={Resources.WorkloadIdentityFederationSubjectLabel}
    >
        <TextField
            value={this._federationSubject()}
            inputType={"text"}
            readOnly
            className="federation-subject-input"
            suffixIconProps={{
                onClick: () => this._copyFederationSubject(),
                iconName: "Copy"
            }}
        />

        </FormItem>

    private _renderFederationIssuer = () => <FormItem
        className={"endpoint-editor-panel-text-box"}
        label={Resources.WorkloadIdentityFederationIssuerLabel}
        ariaLabel={Resources.WorkloadIdentityFederationIssuerLabel}
    >
        <TextField
            value={this._federationIssuer()}
            inputType={"text"}
            readOnly
            className="federation-issuer-input"
            suffixIconProps={{
                onClick: () => this._copyFederationIssuer(),
                iconName: "Copy"
            }}
        />

        </FormItem>



    private _federationSubject() {
        const currentSubject = this._originalEndpoint.authorization.parameters[EndpointConstants.AzureEndpointConstants.WorkloadIdentityFederationSubject];
        if (this._hasFederationSubjectChanged()) {
           const { organization, project } = this._parseSubject(currentSubject);
           return `${scheme}${organization}${valueComponentSeparator}${project}${valueComponentSeparator}${this.props.draftEndpoint.name}`;
        } else {
            return currentSubject;
        }
    }

    private _parseSubject(subject: string) {
        let value = subject.substring(scheme.length);
        let values = value.split(valueComponentSeparator);

        return {
            organization: values[0],
            project: values[1],
            endpointName: values[2]
        };
    }

    private _federationIssuer() {
        return this._originalEndpoint.authorization.parameters[EndpointConstants.AzureEndpointConstants.WorkloadIdentityFederationIssuer];
    }

    private _hasFederationSubjectChanged() {
        if (!this._originalEndpoint || !this._originalEndpoint.serviceEndpointProjectReferences) {
            return false;
        }

        // There can be no two endpoint with the same name in the same project, so it is enough to check the name.
        // The name here is the actual name of this particular shared connection.
        const originalName = this._originalEndpoint.name;
        const currentSubject = this._originalEndpoint.authorization.parameters[EndpointConstants.AzureEndpointConstants.WorkloadIdentityFederationSubject];
        const { endpointName } = this._parseSubject(currentSubject);

        if (!this._originalEndpoint.isShared || Utils_String.equals(originalName, endpointName, false)) {
            return this._originalEndpoint.name !== this.props.draftEndpoint.name;
        }

        return false;
    }

    private _copyFederationSubject() {
        copyToClipboard(this._federationSubject());
    }

    private _copyFederationIssuer() {
        copyToClipboard(this._federationIssuer());
    }

    private _showSuccessToast = () => {
        const messagesService: IGlobalMessagesService = this.context.pageContext.getService<IGlobalMessagesService>("IGlobalMessagesService");
        messagesService.addToast({
            message: Resources.ServiceEndpointConfigurationSuccessful,
            duration: 10000,
            forceOverrideExisting: true
        });
    }

    private _onValidationChange = (descriptor: InputDescriptor, isValid: boolean, isPresent: boolean) =>
        {
            if (!isValid && isPresent) {
                this._malformedInputs.value.add(descriptor.id);
            } else {
                this._malformedInputs.value.delete(descriptor.id);
            }

            this.setTimeout(() => {
                if (this.state.isDraftValid && this._malformedInputs.value.size > 0) {
                    this.setState({ isDraftValid: false });
                } else if (!this.state.isDraftValid && this._malformedInputs.value.size === 0) {
                    this.setState({ isDraftValid: true });
                }
            });
        }

    private _updateInvalidInputsCount = (invalidInputCount: number) =>
        {
            this._invalidInputsCount.value += invalidInputCount;
            this.setTimeout(() => {
                if (this.state.isEndpointValid && this._invalidInputsCount.value > 0) {
                    this.setState({ isEndpointValid: false });
                } else if (!this.state.isEndpointValid && this._invalidInputsCount.value === 0) {
                    this.setState({ isEndpointValid: true });
                }
            });
        }

    private _closePanel = (endpoint: ServiceEndpoint) =>  setTimeout(() => {
        this.props.closePanel(endpoint);
    })

    private _onEndpointEditorStoreChange = () => {
        const storeState = this._endpointEditorStore.getState();
        const savedSuccessfully = this.state.saveInProgress && !storeState.setupInProgress && !storeState.saveInProgress && !storeState.errorMessage;
        const setupSuccessfully = this.state.setupInProgress && !storeState.setupInProgress && !storeState.errorMessage;

        if (savedSuccessfully || setupSuccessfully) {
            switch (this.state.creationStep) {
                case CreationStep.CREATE_DRAFT:
                    this._originalEndpoint = cloneEndpoint(this.props.draftEndpoint);
                    this.setState({
                        creationStep: CreationStep.FINALIZE_DRAFT,
                        isEndpointValid: true
                    });
                    break;
                case CreationStep.FINALIZE_DRAFT:
                    if (this.state.lastAction === LastAction.VERIFY_AND_SAVE) {
                        this._showSuccessToast();
                    }
                    this._closePanel(storeState.serviceEndpoint);
                    break;
                case CreationStep.EDIT:
                    this._closePanel(storeState.serviceEndpoint);
                    break;
                default:
                    break;
            }
        }

        this.setState({
            isError: storeState.isError,
            errorMessage: storeState.errorMessage,
            activityId: storeState.activityId,
            saveInProgress: storeState.saveInProgress,
            setupInProgress: storeState.setupInProgress,
            verifyInProgress: storeState.verifyInProgress
        });
    }

    private _handleValidAndDirtyStateChangeForEndpointDetails = (isValid?: boolean, isDirty?: boolean) => {
        if (isValid !== undefined) {
            this.setState({
                isEndpointValid: isValid
            });
        }
    }

    private _cleanEmptyValues = (obj: { [key: string]: string; }) => {
        for (const key in obj) {
            const value = obj[key];
            if (value === null || value.trim() === ""){
                delete obj[key];
            }
        }
    }

    private _saveServiceEndpoint = (isUpdate: boolean, isDraft: boolean, verifyBeforeSave: boolean = true) => {
        // don't save twice in the second screen - keep the original one and compare
        const savedEndpoint = cloneEndpoint(this.props.draftEndpoint);

        if (isDraft) {
            if (this._endpointChanged(savedEndpoint)) {
                // Drafts are legal as long as values are either set correctly or not set at all
                this._cleanEmptyValues(savedEndpoint.data);
                this._cleanEmptyValues(savedEndpoint.authorization.parameters);
                savedEndpoint.data[EndpointConstants.AzureEndpointConstants.IsDraft] = "True";

                this.setState({ lastAction: LastAction.SAVE_AS_DRAFT });
                this._endpointEditorActionCreator.customSaveServiceEndpoint(savedEndpoint, this.props.allowAllPipelinesFlagState, isUpdate)
                    .then(this.props.onRefresh);
            } else {
                this.props.closePanel(this._originalEndpoint);
            }
        } else {
            delete savedEndpoint.data[EndpointConstants.AzureEndpointConstants.IsDraft];
            if (verifyBeforeSave) {
                this.setState({ lastAction: LastAction.VERIFY_AND_SAVE });
                this._endpointEditorActionCreator.verifyAndSaveServiceEndpoint(savedEndpoint, Constants.TestConnectionDataSource, this.props.allowAllPipelinesFlagState, isUpdate);
            } else {
                this.setState({ lastAction: LastAction.SAVE });
                this._endpointEditorActionCreator.customSaveServiceEndpoint(savedEndpoint, this.props.allowAllPipelinesFlagState, isUpdate);
            }
        }
    }

    private _endpointChanged = (endpoint: ServiceEndpoint) =>
        !Utils_Object.shallowCompare(endpoint.authorization.parameters, this._originalEndpoint.authorization.parameters) ||
        !Utils_Object.shallowCompare(endpoint.data, this._originalEndpoint.data) ||
        !Utils_Object.shallowCompare(endpoint.name, this._originalEndpoint.name) ||
        !Utils_Object.shallowCompare(endpoint.description, this._originalEndpoint.description)

    private _originalEndpoint: ServiceEndpoint;
    private _endpointEditorActionCreator: EndpointsEditorActionsCreator;
    private _endpointEditorStore: EndpointsEditorStore;
    private _invalidInputsCount: ObservableValue<number>;
    private _malformedInputs: ObservableValue<Set<string>>;
    private _projectId: string;
    private _actionCreatorKey = AzureRMKeys.AZURERM_INSTANCE_KEY;
}
